import { cn, Table, useSort } from "@kyan/ui";
import { AnimatePresence, motion } from "motion/react";

import { EmptyState } from "#/components/Contents/components/EmptyState/EmptyState";
import { useAutoExpandRows } from "#/hooks/useAutoExpandRows";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { useSharePnlCardsModal } from "#/store/app/sharePnlCardModal/sharePnlCardsModal";
import { usePositions } from "#/store/wallet/account/positions";
import { type Position } from "#/types/positions";

import { OptionPositions } from "../OptionPositions/OptionPositions";
import {
  defaultSort,
  getOuterColumns,
} from "../OptionPositions/OptionPositions.constants";
import { PerpPositions } from "../PerpPositions/PerpPositions";
import { totalRowInstrument } from "./AllPositions.constants";
import { getAllPositionRows } from "./AllPositions.utils";

export interface AllPositionsProps {
  isPortfolioVariant?: boolean;
}

export const AllPositions: React.FC<AllPositionsProps> = ({
  isPortfolioVariant,
}) => {
  const { positions } = usePositions();
  const { open } = useSharePnlCardsModal();
  const { market } = usePositionsDrawerMarket();
  const { sort, setSort } = useSort<Position>({
    defaultSort,
  });

  const allPositions = getAllPositionRows(positions);
  const isEmpty = !allPositions.length;

  const { openRowIds, onRowClick } = useAutoExpandRows<Position>({
    dataLength: positions.length,
  });

  const outerColumns = getOuterColumns({
    openSharePnlCardsModal: open,
  });

  return (
    <Table
      isServerSide
      sort={sort}
      data={allPositions}
      columns={outerColumns}
      openRowIds={openRowIds}
      defaultSort={defaultSort}
      emptyState={
        <EmptyState
          underlying={market.underlying}
          shouldShowAction={isPortfolioVariant}
          description={`Your ${market.underlying} positions will show here`}
        />
      }
      classes={{
        wrapper: cn("bg-background flex flex-col", {
          "flex-1": !isEmpty,
          "h-full": isEmpty,
        }),
        table: cn("flex w-fit flex-col", {
          "flex-1 overflow-hidden": !isEmpty,
          "h-full": isEmpty,
          "border-b-0": isEmpty && !isPortfolioVariant,
        }),
        thead: "sticky top-0 bg-buttonBg border-b border-divider",
        tbody: cn("text-textPrimary overflow-y-auto", {
          "flex-1": isEmpty,
          "border-divider border-b": !isEmpty && !isPortfolioVariant,
        }),
        row: "bg-buttonHover first:border-t-0 cursor-pointer",
        loading: {
          wrapper: "first:border-t-0",
        },
      }}
      onSortChange={setSort}
      onRowClick={onRowClick}
      renderRowExpansion={({ row, isOpen }) => {
        const isOptionsRow = row.instrumentType === "options";
        const isPerpsRow =
          row.instrumentType === "perps" &&
          row.instrument !== totalRowInstrument;

        return (
          <AnimatePresence>
            {isOpen && (
              <motion.div
                exit={{ height: 0 }}
                initial={{ height: 0 }}
                className="overflow-hidden"
                animate={{ height: "auto" }}
                transition={{ duration: 0.3 }}
              >
                {isOptionsRow && (
                  <OptionPositions
                    isAllVariant
                    sort={sort}
                    onSortChange={setSort}
                  />
                )}

                {isPerpsRow && <PerpPositions isAllVariant />}
              </motion.div>
            )}
          </AnimatePresence>
        );
      }}
    />
  );
};
